import { reactive, readonly } from 'vue'
import { getNotifRegistration } from '@/services/notifications'
import { deleteWebPushSubscription, getWebPushPublicKey, saveWebPushSubscription } from '@/services/wootdashWebPush'

// Web Push (notificação nativa do Windows mesmo com o painel fechado). O navegador se inscreve no serviço de push
// dele (Google no Chrome, Microsoft no Edge…) pelo sw-notif.js e o backend guarda a inscrição pro agente. Quem liga
// é o agente, em Meu perfil › Notificações no Windows.
//
// - Login: se este navegador já estava inscrito, reconecta sozinho pro agente que entrou (syncWebPush).
// - Logout: desvincula este navegador do agente que saiu (detachWebPush) — a inscrição do navegador continua, pra
//   voltar sozinha no próximo login, mas ninguém recebe aviso de quem não está logado aqui.

export type WebPushStatus = 'unsupported' | 'insecure' | 'denied' | 'off' | 'on'

const state = reactive({
  status: 'off' as WebPushStatus,
  endpoint: null as string | null,
  busy: false,
  problem: null as string | null,
})

export function webPushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/** Chave VAPID (base64url) → bytes, como o pushManager.subscribe pede. */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(padded)
  // ArrayBuffer explícito: o pushManager.subscribe só aceita BufferSource de ArrayBuffer (não SharedArrayBuffer).
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

/** "Chrome no Windows" — pro agente reconhecer o computador na lista. */
export function describeDevice(ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : ''): string {
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Navegador'
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'macOS' : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : 'outro sistema'
  return `${browser} no ${os}`
}

function toPayload(sub: PushSubscription) {
  const json = sub.toJSON()
  return { endpoint: sub.endpoint, p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '', device: describeDevice() }
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await getNotifRegistration()
  return reg ? reg.pushManager.getSubscription() : null
}

/** Lê o estado deste navegador (sem pedir nada). */
export async function refreshWebPush(): Promise<void> {
  if (!webPushSupported()) { state.status = 'unsupported'; return }
  if (!window.isSecureContext) { state.status = 'insecure'; return }
  if (Notification.permission === 'denied') { state.status = 'denied'; state.endpoint = null; return }
  const sub = await currentSubscription()
  state.endpoint = sub?.endpoint ?? null
  state.status = sub && Notification.permission === 'granted' ? 'on' : 'off'
}

/** Liga neste navegador: pede permissão, inscreve no serviço de push e grava no servidor. */
export async function enableWebPush(token: string): Promise<boolean> {
  state.problem = null
  if (!webPushSupported()) { state.status = 'unsupported'; return false }
  state.busy = true
  try {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      state.status = permission === 'denied' ? 'denied' : 'off'
      state.problem = permission === 'denied' ? null : 'A permissão de notificação não foi concedida.'
      return false
    }
    const reg = await getNotifRegistration()
    const publicKey = await getWebPushPublicKey(token)
    if (!reg || !publicKey) {
      state.problem = 'Não deu pra preparar as notificações agora. Tente de novo.'
      return false
    }
    let sub = await reg.pushManager.getSubscription()
    // Inscrição feita com outra chave (servidor trocou as chaves VAPID): refaz.
    if (sub && !sameKey(sub, publicKey)) { await sub.unsubscribe(); sub = null }
    sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) })
    const problem = await saveWebPushSubscription(token, toPayload(sub))
    if (problem) { state.problem = problem; return false }
    state.endpoint = sub.endpoint
    state.status = 'on'
    return true
  } catch (err) {
    state.problem = `Não deu pra ligar as notificações: ${(err as Error).message}`
    return false
  } finally {
    state.busy = false
  }
}

/** Desliga neste navegador (sai do serviço de push e do servidor). */
export async function disableWebPush(token: string): Promise<void> {
  state.busy = true
  state.problem = null
  try {
    const sub = await currentSubscription()
    if (sub) {
      await deleteWebPushSubscription(token, { endpoint: sub.endpoint })
      await sub.unsubscribe().catch(() => undefined)
    }
    state.endpoint = null
    state.status = Notification.permission === 'denied' ? 'denied' : 'off'
  } finally {
    state.busy = false
  }
}

/** Login: este navegador já estava inscrito e com permissão → reconecta pro agente que entrou (sem perguntar nada). */
export async function syncWebPush(token: string): Promise<void> {
  try {
    if (!webPushSupported() || !window.isSecureContext || Notification.permission !== 'granted') return
    const sub = await currentSubscription()
    if (!sub) return
    const publicKey = await getWebPushPublicKey(token)
    if (publicKey && !sameKey(sub, publicKey)) return // chaves trocadas: o agente liga de novo no perfil
    await saveWebPushSubscription(token, toPayload(sub))
    state.endpoint = sub.endpoint
    state.status = 'on'
  } catch { /* best-effort: sem push, o painel continua avisando */ }
}

/** Logout: este navegador para de receber os avisos do agente que saiu (a inscrição do navegador fica). */
export async function detachWebPush(token: string): Promise<void> {
  try {
    const sub = webPushSupported() ? await currentSubscription() : null
    if (sub) await deleteWebPushSubscription(token, { endpoint: sub.endpoint })
  } catch { /* best-effort */ }
}

function sameKey(sub: PushSubscription, publicKey: string): boolean {
  const current = sub.options?.applicationServerKey
  if (!current) return true
  const a = new Uint8Array(current)
  const b = urlBase64ToUint8Array(publicKey)
  return a.length === b.length && a.every((v, i) => v === b[i])
}

export function useWebPush() {
  return { webPush: readonly(state) }
}
